import React, { Fragment } from 'react';
import CheckIcon from '@material-ui/icons/Check';
import {
  ColoredHeaderBanner,
  Wrapper,
  TopContainer,
  FooterContainer,
  HeaderTitle,
  HeaderSection,
  HeaderSubCopy,
  ButtonsWrapper,
  HeaderButton,
  LocationCopyWrapper,
  LastUpdatedDate,
  SectionHalf,
  Copy,
  ColumnTitle,
  SectionColumn,
  LevelDescription,
  ThermometerContainer,
  ThermometerRow,
} from 'components/LocationPage/LocationPageHeader.style';
import { useEmbed } from 'common/utils/hooks';
import { LOCATION_SUMMARY_LEVELS } from 'common/metrics/location_summary';
import { Level } from 'common/level';
import { COLOR_MAP } from 'common/colors';
import { useModelLastUpdatedDate } from 'common/utils/model';
import { STATES_WITH_DATA_OVERRIDES } from 'common/metrics/hospitalizations';
import { Projections } from 'common/models/Projections';
import { formatUtcDate } from 'common/utils';
import { isNull } from 'util';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';
import LocationHeaderStats from 'components/SummaryStats/LocationHeaderStats';
import { LEVEL_COLOR } from 'common/colors';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Metric } from 'common/metric';
import ExternalLink from 'components/ExternalLink';

const NewFeatureCopy = (props: {
  locationName: string;
  onNewUpdateClick: () => void;
}) => {
  return (
    <Copy isUpdateCopy>
      <strong>Explore COVID Trends.</strong> View raw case, death, and
      hospitalization data for {props.locationName} and compare it against other
      locations in our new Trends chart.{' '}
      <span
        style={{ whiteSpace: 'nowrap' }}
        onClick={props.onNewUpdateClick || noop}
      >
        See it below
      </span>
    </Copy>
  );
};

const LocationPageHeading = (props: { projections: Projections }) => {
  const { isEmbed } = useEmbed();

  const displayName = props.projections.countyName ? (
    <>
      <strong>{props.projections.countyName}, </strong>
      <a
        href={`${
          isEmbed ? '/embed' : ''
        }/us/${props.projections.stateCode.toLowerCase()}`}
      >
        {props.projections.stateCode}
      </a>
    </>
  ) : (
    <strong>{props.projections.stateName}</strong>
  );

  return <span>{displayName}</span>;
};

const noop = () => {};

const LocationPageHeader = (props: {
  projections: Projections;
  condensed?: boolean;
  stats: { [key: string]: number | null };
  onMetricClick: (metric: Metric) => void;
  onHeaderShareClick: () => void;
  onHeaderSignupClick: () => void;
  isMobile?: boolean;
  onNewUpdateClick: () => void;
}) => {
  const hasStats = !!Object.values(props.stats).filter(
    (val: number | null) => !isNull(val),
  ).length;

  //TODO (chelsi): get rid of this use of 'magic' numbers
  const headerTopMargin = !hasStats ? -202 : -218;
  const headerBottomMargin = !hasStats ? 0 : 0;

  const locationName =
    props.projections.countyName || props.projections.stateName;

  const alarmLevel = props.projections.getAlarmLevel();

  const levelInfo = LOCATION_SUMMARY_LEVELS[alarmLevel];

  const fillColor =
    alarmLevel !== Level.UNKNOWN ? levelInfo.color : COLOR_MAP.GRAY.LIGHT;

  const { isEmbed } = useEmbed();

  const verified = STATES_WITH_DATA_OVERRIDES.includes(
    props.projections.stateName,
  );

  const isVerifiedState = !props.projections.isCounty && verified;

  const lastUpdatedDate: Date | null = useModelLastUpdatedDate() || new Date();
  const lastUpdatedDateString =
    lastUpdatedDate !== null ? formatUtcDate(lastUpdatedDate) : '';

  const thresholdUnknown = alarmLevel === Level.UNKNOWN;

  const thermometerContent = [
    {
      level: Level.CRITICAL,
      color: `${LEVEL_COLOR[Level.CRITICAL]}`,
    },
    {
      level: Level.HIGH,
      color: `${LEVEL_COLOR[Level.HIGH]}`,
    },
    {
      level: Level.MEDIUM,
      color: `${LEVEL_COLOR[Level.MEDIUM]}`,
    },
    {
      level: Level.LOW,
      color: `${LEVEL_COLOR[Level.LOW]}`,
    },
  ];

  const usingCMSData =
    props.projections.primary.testPositiveRateSource === 'CMSTesting';

  return (
    <Fragment>
      <ColoredHeaderBanner bgcolor={fillColor} />
      <Wrapper
        condensed={props.condensed}
        headerTopMargin={headerTopMargin}
        headerBottomMargin={headerBottomMargin}
      >
        <TopContainer>
          <HeaderSection>
            <LocationCopyWrapper>
              <HeaderTitle isEmbed={isEmbed}>
                <LocationPageHeading projections={props.projections} />
              </HeaderTitle>
            </LocationCopyWrapper>
            <ButtonsWrapper>
              <HeaderButton onClick={props.onHeaderShareClick || noop}>
                <ShareOutlinedIcon />
                Share
              </HeaderButton>
              <HeaderButton onClick={props.onHeaderSignupClick || noop}>
                <NotificationsNoneIcon />
                Receive alerts
              </HeaderButton>
            </ButtonsWrapper>
          </HeaderSection>
          <HeaderSection>
            <SectionHalf>
              <ThermometerContainer>
                {thermometerContent.map((row, i) => {
                  const isCurrentLevel = row.level === alarmLevel;
                  return (
                    <ThermometerRow
                      color={row.color}
                      thresholdUnknown={thresholdUnknown}
                      isCurrentLevel={isCurrentLevel}
                    />
                  );
                })}
              </ThermometerContainer>
              <SectionColumn>
                <ColumnTitle>Covid risk level</ColumnTitle>
                <LevelDescription>{levelInfo.summary}</LevelDescription>
                <Copy>{levelInfo.detail(locationName)}</Copy>
              </SectionColumn>
            </SectionHalf>
            <SectionHalf>
              <InfoOutlinedIcon />
              {usingCMSData ? (
                <SectionColumn isUpdateCopy>
                  <ColumnTitle isUpdateCopy>Update</ColumnTitle>
                  <Copy isUpdateCopy={true}>
                    As of November 4th, we are using positive test rate data for{' '}
                    {locationName} from the U.S. Department of Health & Human
                    Services as aggregated by the Centers for Medicare &
                    Medicaid Services. See{' '}
                    <ExternalLink href="https://docs.google.com/presentation/d/1XmKCBWYZr9VQKFAdWh_D7pkpGGM_oR9cPjj-UrNdMJQ/edit">
                      our data sources
                    </ExternalLink>{' '}
                    for more info.
                  </Copy>
                </SectionColumn>
              ) : (
                <SectionColumn isUpdateCopy>
                  <ColumnTitle isUpdateCopy>New feature</ColumnTitle>
                  <NewFeatureCopy
                    locationName={locationName}
                    onNewUpdateClick={props.onNewUpdateClick}
                  />
                </SectionColumn>
              )}
            </SectionHalf>
          </HeaderSection>
          <LocationHeaderStats
            stats={props.stats}
            onMetricClick={props.onMetricClick}
            isMobile={props.isMobile}
            isHeader={true}
          />
        </TopContainer>
        <FooterContainer>
          {props.projections.isCounty && !isEmbed && (
            <HeaderSubCopy>
              <span>Updated {lastUpdatedDateString} · </span>
              <span>County data is currently in beta. </span>
              <span>
                Because counties don’t report hospitalizations, our forecasts
                may not be as accurate. See something wrong?{' '}
              </span>
              <a
                href="mailto:info@covidactnow.org?subject=[Website%20Feedback]"
                target="_blank"
                rel="noopener noreferrer"
              >
                Email us
              </a>
              .
            </HeaderSubCopy>
          )}
          {!props.projections.isCounty && !isEmbed && (
            <HeaderSubCopy>
              {verified && (
                <Fragment>
                  <CheckIcon htmlColor="#00D474" />
                  <span>Government verified data</span>
                  {!props.isMobile && <span> · </span>}
                </Fragment>
              )}
              <LastUpdatedDate isVerifiedState={isVerifiedState}>
                Updated {lastUpdatedDateString}
              </LastUpdatedDate>
            </HeaderSubCopy>
          )}
        </FooterContainer>
      </Wrapper>
    </Fragment>
  );
};

export default LocationPageHeader;
